import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";

// Query Keys
export const queryKeys = {
  profile: ["dashboard", "profile"] as const,
  secteurs: ["dashboard", "secteurs"] as const,
  matchingProfiles: (entityType: string, page: number, selections?: any[]) =>
    ["dashboard", "matching", entityType, page, selections] as const,
  addressSuggestions: (query: string) =>
    ["addresses", "suggestions", query] as const,
  campaigns: (page?: number, perPage?: number, tag?: string) =>
    ["campaigns", page, perPage, tag] as const,
  campaignTags: ["campaigns", "tags"] as const,
  campaign: (id: number) => ["campaigns", id] as const,
};

// Profile Query
export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const { data } = await axios.get("/app/dashboard/profile", {
        headers: { Accept: "application/json" },
      });
      return data;
    },
  });
}

// Secteurs Query
export function useSecteurs() {
  return useQuery({
    queryKey: queryKeys.secteurs,
    queryFn: async () => {
      const { data } = await axios.get("/app/dashboard/secteurs", {
        headers: { Accept: "application/json" },
      });
      return data;
    },
  });
}

// Matching Profiles Query
interface MatchingProfilesResponse {
  [key: string]: any[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
  matching_criteria?: string;
  missing_fields?: string[];
}

export function useMatchingProfiles(
  entityType: "candidats" | "enseignes" | "villes" | "locations",
  page: number = 1,
  selections: Array<{ id: string; type: string }> = [],
  enabled: boolean = true,
) {
  const fetchUrl = `/app/dashboard/matching_${entityType}`;

  return useQuery({
    queryKey: queryKeys.matchingProfiles(entityType, page, selections),
    queryFn: async (): Promise<MatchingProfilesResponse> => {
      const params = new URLSearchParams({ page: page.toString() });

      // Add selections as query param if present
      if (selections.length > 0) {
        params.append("selections", JSON.stringify(selections));
      }

      const { data } = await axios.get(`${fetchUrl}?${params}`, {
        headers: { Accept: "application/json" },
      });
      return data;
    },
    enabled, // Only fetch when enabled is true
    staleTime: Infinity, // Keep data forever when disabled
    gcTime: Infinity, // Don't garbage collect cached data
  });
}

// Address Suggestions Query
export function useAddressSuggestions(query: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.addressSuggestions(query),
    queryFn: async () => {
      const { data } = await axios.get(
        `/addresses?q=${encodeURIComponent(query)}`,
        {
          headers: { Accept: "application/json" },
        },
      );
      return data;
    },
    enabled: enabled && query.length >= 3,
    staleTime: 1000 * 60, // 1 minute
  });
}

// Update Profile Mutation
interface UpdateProfileParams {
  profileId: number;
  profileType: string;
  data: Record<string, any>;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileId,
      profileType,
      data,
    }: UpdateProfileParams) => {
      const { data: response } = await axios.patch(
        `/app/profile/dashboard_update`,
        {
          [`profile_${profileType}`]: data,
        },
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate all dashboard queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Profil mis à jour avec succès");
    },
    onError: (error: any) => {
      console.error("Error updating profile:", error);
      toast.error(
        error.response?.data?.message || "Erreur lors de la mise à jour",
      );
    },
  });
}

// Create/Select Address Mutation
interface CreateAddressParams {
  endpoint: string;
  placeId: string;
}

export function useCreateAddress() {
  return useMutation({
    mutationFn: async ({ endpoint, placeId }: CreateAddressParams) => {
      const { data } = await axios.post(endpoint, {
        place_id: placeId,
      });
      return data;
    },
    onError: (error: any) => {
      console.error("Error selecting address:", error);
      toast.error(
        error.response?.data?.message ||
          "Erreur lors de la sélection de l'adresse",
      );
    },
  });
}

// Campaigns Query
interface CampaignsResponse {
  campaigns: any[];
  pagination: {
    current_page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

export function useCampaigns(
  page: number = 1,
  perPage: number = 3,
  tag?: string,
) {
  return useQuery({
    queryKey: queryKeys.campaigns(page, perPage, tag),
    queryFn: async (): Promise<CampaignsResponse> => {
      const params: any = { page, per_page: perPage };
      if (tag) {
        params.tag = tag;
      }
      const { data } = await axios.get("/app/campaigns", {
        params,
        headers: { Accept: "application/json" },
      });
      return data;
    },
  });
}

// Campaign Tags Query
export function useCampaignTags() {
  return useQuery({
    queryKey: queryKeys.campaignTags,
    queryFn: async (): Promise<string[]> => {
      const { data } = await axios.get("/app/campaigns/tags", {
        headers: { Accept: "application/json" },
      });
      return data;
    },
  });
}

// Single Campaign Query
export function useCampaign(id: number) {
  return useQuery({
    queryKey: queryKeys.campaign(id),
    queryFn: async () => {
      const { data } = await axios.get(`/app/campaigns/${id}`, {
        headers: { Accept: "application/json" },
      });
      return data;
    },
    enabled: !!id,
  });
}

// Generate Campaign Title Mutation
interface GenerateTitleParams {
  items: Array<{ id: string; type: string }>;
}

export function useGenerateCampaignTitle() {
  return useMutation({
    mutationFn: async ({ items }: GenerateTitleParams) => {
      const { data } = await axios.post("/app/campaigns/generate_title", {
        items,
      });
      if (data.error) {
        throw new Error(data.error);
      }
      return data.title as string;
    },
    onError: (error: any) => {
      console.error("Error generating title:", error);
      toast.error("Erreur lors de la génération du titre");
    },
  });
}

// Create Campaign Mutation
interface CreateCampaignParams {
  title: string;
  tags?: string[];
  items: Array<{ id: string; type: string }>;
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ title, tags, items }: CreateCampaignParams) => {
      const { data } = await axios.post("/app/campaigns", {
        campaign: { title, tags: tags || [] },
        items,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Projet enregistré avec succès");
    },
    onError: (error: any) => {
      console.error("Error creating campaign:", error);
      toast.error(
        error.response?.data?.errors?.join(", ") ||
          "Erreur lors de l'enregistrement du projet",
      );
    },
  });
}

// Update Campaign Mutation
interface UpdateCampaignParams {
  id: number;
  title: string;
  tags?: string[];
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title, tags }: UpdateCampaignParams) => {
      const { data } = await axios.patch(`/app/campaigns/${id}`, {
        campaign: { title, tags: tags || [] },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Projet mis à jour avec succès");
    },
    onError: (error: any) => {
      console.error("Error updating campaign:", error);
      toast.error(
        error.response?.data?.errors?.join(", ") ||
          "Erreur lors de la mise à jour du projet",
      );
    },
  });
}

// Delete Campaign Mutation
export function useDeleteCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId: number) => {
      await axios.delete(`/app/campaigns/${campaignId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Projet supprimé avec succès");
    },
    onError: (error: any) => {
      console.error("Error deleting campaign:", error);
      toast.error(
        error.response?.data?.message ||
          "Erreur lors de la suppression du projet",
      );
    },
  });
}
